
import React, { useEffect, useState } from "react";
import axios from "axios";
import Card from "components/card";
import Chart from "react-apexcharts";
import { API_BASE_URL } from "../../../../config";

const RevenueStatsCard = () => {
  const [stats, setStats] = useState([]);
  const [viewType, setViewType] = useState("monthly");
  const [chartData, setChartData] = useState({ labels: [], data: [] });
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(true);

  // ✅ Fetch API
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${API_BASE_URL}/api/finalbillRecord/total-revenue-stats`,
          {
            headers: {
              token,
            },
          }
        );

        if (res.data.success) {
          // console.log("Raw API Data:", res.data.result);
          setStats(res.data.result);
        } else {
          // console.error("API Error:", res.data.message);
        }
      } catch (err) {
        // console.error("Error fetching stats:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // ✅ Process data based on view type - FIXED YEARLY VIEW
  useEffect(() => {
    if (!stats.length) return;

    // console.log("Processing data for viewType:", viewType);
    
    let labels = [];
    let data = [];
    let total = 0;

    if (viewType === "monthly") {
      // Get latest year for monthly view
      const latestYear = stats[stats.length - 1];
      
      // Sort months and create all 12 months with 0 revenue if missing
      const monthlyData = Array.from({ length: 12 }, (_, i) => {
        const monthNum = i + 1;
        const monthData = latestYear.months.find(m => m.month === monthNum);
        return {
          month: monthNum,
          revenue: monthData ? monthData.revenue : 0
        };
      });

      labels = monthlyData.map((m) => {
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                           'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return monthNames[m.month - 1];
      });
      data = monthlyData.map((m) => m.revenue);
      total = monthlyData.reduce((a, b) => a + b.revenue, 0);
      
    } else if (viewType === "quarterly") {
      // Get latest year for quarterly view
      const latestYear = stats[stats.length - 1];
      
      // Group months into quarters and sum their revenue
      const quarterlyData = Array.from({ length: 4 }, (_, i) => {
        const quarterNum = i + 1;
        const quarterMonths = latestYear.months.filter(m => 
          Math.ceil(m.month / 3) === quarterNum
        );
        const quarterRevenue = quarterMonths.reduce((sum, month) => sum + month.revenue, 0);
        return {
          quarter: quarterNum,
          revenue: quarterRevenue
        };
      });

      labels = quarterlyData.map((q) => `Q${q.quarter}`);
      data = quarterlyData.map((q) => q.revenue);
      total = quarterlyData.reduce((a, b) => a + b.revenue, 0);
      
    } else if (viewType === "yearly") {
      // FIXED: Show data for all available years properly
      labels = stats.map(year => year._id.toString());
      data = stats.map(year => year.yearlyRevenue);
      // For yearly, show total of current year or latest year, not sum of all years
      const currentYear = stats[stats.length - 1];
      total = currentYear ? currentYear.yearlyRevenue : 0;
    }

    // console.log("Processed chart data:", { labels, data, total });
    setChartData({ labels, data });
    setTotalAmount(total);
  }, [viewType, stats]);

  // ✅ Chart configuration - FIXED TO SHOW X AND Y AXIS
  const chartOptions = {
    chart: {
      type: "area",
      toolbar: { show: false },
      zoom: { enabled: false },
      background: 'transparent',
      fontFamily: 'Inter, sans-serif',
      sparkline: {
        enabled: false
      }
    },
    stroke: {
      curve: "smooth",
      width: 2,
      colors: ["#FCD34D"], // Yellow color similar to Image 2
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        type: 'vertical',
        opacityFrom: 0.3,
        opacityTo: 0.05,
        stops: [0, 100],
        colorStops: [
          {
            offset: 0,
            color: "#FCD34D",
            opacity: 0.3
          },
          {
            offset: 100,
            color: "#FCD34D",
            opacity: 0.05
          }
        ]
      }
    },
    xaxis: {
      categories: chartData.labels,
      labels: {
        show: true, // CHANGED: Show X-axis labels
        style: {
          colors: '#9CA3AF',
          fontSize: '12px',
          fontWeight: 400,
        },
        rotate: 0,
      },
      axisBorder: { 
        show: true,
        color: '#E5E7EB',
        height: 1,
      },
      axisTicks: { 
        show: true,
        color: '#E5E7EB',
        height: 4,
      },
    },
    yaxis: {
      show: true, // CHANGED: Show Y-axis
      labels: {
        show: true,
        style: {
          colors: '#9CA3AF',
          fontSize: '12px',
          fontWeight: 400,
        },
        formatter: function (val) {
          // Format large numbers nicely
          if (val >= 10000000) {
            return '₹' + (val / 10000000).toFixed(1) + 'Cr';
          } else if (val >= 100000) {
            return '₹' + (val / 100000).toFixed(1) + 'L';
          } else if (val >= 1000) {
            return '₹' + (val / 1000).toFixed(1) + 'K';
          }
          return '₹' + val;
        }
      },
      axisBorder: {
        show: false
      },
      axisTicks: {
        show: false
      }
    },
    grid: {
      show: true, // CHANGED: Show grid for better readability
      borderColor: '#F3F4F6',
      strokeDashArray: 3,
      xaxis: {
        lines: {
          show: false
        }
      },
      yaxis: {
        lines: {
          show: true
        }
      },
      padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0
      }
    },
    tooltip: {
      enabled: true,
      custom: function({series, seriesIndex, dataPointIndex, w}) {
        const value = series[seriesIndex][dataPointIndex];
        const label = w.globals.labels[dataPointIndex];
        return '<div class="bg-white px-3 py-2 rounded-lg shadow-lg border">' +
               '<div class="text-xs text-gray-600">' + label + '</div>' +
               '<div class="text-sm font-semibold text-gray-900">' +
               new Intl.NumberFormat("en-IN", {
                 style: "currency",
                 currency: "INR",
                 maximumFractionDigits: 2,
               }).format(value) +
               '</div>' +
               '</div>';
      }
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    markers: {
      size: 0,
      strokeWidth: 0,
      hover: {
        size: 4,
        sizeOffset: 2
      }
    }
  };

  const chartSeries = [
    {
      name: "Revenue",
      data: chartData.data || [],
    },
  ];

  if (loading) {
    return (
      <Card extra="rounded-[20px] p-6 bg-white shadow-sm">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card extra="rounded-[20px] p-6 bg-white shadow-sm border border-gray-100">
      {/* Header */}
      <div className="flex flex-row justify-between items-start mb-4">
        <div className="space-y-3">
          <h4 className="text-lg font-medium text-gray-700 dark:text-white">
            Revenue Statistics
          </h4>

          {/* Dropdown - styled like Image 2 */}
          <select
            value={viewType}
            onChange={(e) => setViewType(e.target.value)}
            className="text-sm border border-gray-200 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-yellow-400 focus:border-yellow-400 text-gray-600"
          >
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>

        {/* Total Amount - styled like Image 2 */}
        <div className="text-right">
          <div className="text-2xl font-bold text-gray-900 dark:text-white">
            {new Intl.NumberFormat("en-IN", {
              style: "currency",
              currency: "INR",
              maximumFractionDigits: 2,
            }).format(totalAmount)}
          </div>
          <div className="text-sm text-gray-500 mt-0.5 capitalize">
            {viewType === "monthly" ? "This month" : viewType === "quarterly" ? "This quarter" : "This year"}
          </div>
        </div>
      </div>

      {/* Chart Container - matches Image 2 dimensions */}
      <div className="w-full h-[220px] mt-6">
        {chartData.data && chartData.data.length > 0 ? (
          <Chart
            options={chartOptions}
            series={chartSeries}
            type="area"
            height="220"
            width="100%"
          />
        ) : (
          <div className="flex justify-center items-center h-full">
            <div className="text-center">
              <div className="text-gray-400 text-lg mb-2">📊</div>
              <div className="text-gray-500 text-sm">No data available</div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default RevenueStatsCard;